import styled from "styled-components";

// Helper function to get theme color with fallback
const getThemeColor = (theme, colorName, fallback) => {
  if (!theme || !theme.colors) return fallback;
  return theme.colors[colorName] || fallback;
};

// Define breakpoint
const device = {
  mobileS: '(max-width: 767px)'
};

export const Container = styled.div`
  display: flex;
  height: 100vh;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgBase', '#f9fafb')};
  overflow: hidden;
  font-family: Arial, Helvetica, sans-serif;
  
  @media ${device.mobileS} {
    flex-direction: column;
  }
`;

export const Sidebar = styled.aside`
  width: 280px;
  min-width: 280px;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  border-right: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#e5e7eb')};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  flex-shrink: 0;

  @media ${device.mobileS} {
    width: 100%;
    min-width: 100%;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#e5e7eb')};
    max-height: 40vh;
  }
`;

export const SidebarHeader = styled.div`
  margin-bottom: 8px;
`;

export const SidebarTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  margin: 0 0 8px 0;
`;

export const SidebarSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  margin: 0;
`;

export const SidebarSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SidebarLabel = styled.h2`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
`;

export const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  border-radius: 8px;
  font-size: 14px;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
    box-shadow: 0 0 0 2px ${({ theme }) => getThemeColor(theme, 'colorPrimaryBg', 'rgba(22, 119, 255, 0.1)')};
  }

  &:hover {
    border-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
  }
`;

export const ScreenList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ScreenButton = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${({ $active, theme }) =>
    $active ? getThemeColor(theme, 'colorPrimaryBg', '#e6f4ff') : 'transparent'};
  color: ${({ $active, theme }) =>
    $active 
      ? getThemeColor(theme, 'colorPrimary', '#1677ff') 
      : getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  font-weight: ${({ $active }) => ($active ? 500 : 400)};

  &:hover {
    background-color: ${({ $active, theme }) =>
      $active 
        ? getThemeColor(theme, 'colorPrimaryBg', '#e6f4ff') 
        : getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
  }
`;

export const MainContent = styled.main`
  flex: 1;
  overflow-y: auto;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgBase', '#f9fafb')};
`;

export const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;

  @media ${device.mobileS} {
    padding: 16px;
  }
`;

export const Header = styled.div`
  margin-bottom: 32px;
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
  margin: 0 0 8px 0;
  
  @media ${device.mobileS} {
    font-size: 24px;
  }
`;

export const PageDescription = styled.p`
  font-size: 16px;
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  margin: 0;
`;

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 32px;

  @media ${device.mobileS} {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const MetricCard = styled.div`
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  padding: 24px;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  ${({ $warning }) => $warning && `
    background-color: #fef2f2;
    border-color: #fecaca;
  `}

  ${({ $success }) => $success && `
    background-color: #f0fdf4;
    border-color: #bbf7d0;
  `}
`;

export const MetricLabel = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  margin: 0 0 12px 0;
`;

export const MetricValue = styled.p`
  font-size: 36px;
  font-weight: 700;
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  margin: 0;
`;

export const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

export const SearchInput = styled.input`
  width: 100%;
  max-width: 530px;
  margin-top: 10px;
  padding: 12px 16px 12px 44px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  border-radius: 12px;
  font-size: 14px;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
    box-shadow: 0 0 0 3px ${({ theme }) => getThemeColor(theme, 'colorPrimaryBg', 'rgba(22, 119, 255, 0.1)')};
  }

  &::placeholder {
    color: ${({ theme }) => getThemeColor(theme, 'colorTextTertiary', 'rgba(0, 0, 0, 0.25)')};
  }
`;

export const SearchIconWrapper = styled.div`
  position: absolute;
  left: 14px;
  top: calc(10px + 12px + 10px);
  transform: translateY(-50%);
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  pointer-events: none;
  display: flex;
  align-items: center;
`;

export const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

export const TabButton = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${({ $active, theme }) =>
    $active 
      ? getThemeColor(theme, 'colorPrimary', '#1677ff') 
      : getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  color: ${({ $active, theme }) =>
    $active 
      ? '#ffffff' 
      : getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};

  &:hover {
    background-color: ${({ $active, theme }) =>
      $active 
        ? getThemeColor(theme, 'colorPrimary', '#1677ff') 
        : getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
    border-color: ${({ $active, theme }) =>
      $active 
        ? getThemeColor(theme, 'colorPrimary', '#1677ff') 
        : getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  }
`;

export const TableContainer = styled.div`
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  overflow: hidden;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHead = styled.thead`
  background-color: ${({ theme }) => getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
  border-bottom: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
`;

export const TableHeader = styled.th`
  text-align: left;
  padding: 16px 24px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
`;

export const TableBody = styled.tbody`
  & tr {
    border-bottom: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
    transition: background-color 0.15s;

    &:hover {
      background-color: ${({ theme }) => getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.02)')};
    }

    &:last-child {
      border-bottom: none;
    }
  }
`;

export const TableCell = styled.td`
  padding: 16px 24px;
  font-size: 14px;
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
`;

export const KeyCell = styled(TableCell)`
  font-family: 'Courier New', monospace;
  font-weight: 500;
  color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
`;

export const EmptyText = styled.span`
  color: ${({ theme }) => getThemeColor(theme, 'colorTextTertiary', 'rgba(0, 0, 0, 0.25)')};
  font-style: italic;
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${({ $status }) => 
    $status === 'complete' ? '#f0fdf4' : '#fef2f2'};
  color: ${({ $status }) => 
    $status === 'complete' ? '#15803d' : '#dc2626'};
`;

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
`;

export const PaginationButton = styled.button`
  padding: 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
    border-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PaginationInfo = styled.span`
  font-size: 14px;
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  
  & strong {
    font-weight: 600;
  }
`;

export const EmptyState = styled.td`
  padding: 48px 24px;
  text-align: center;
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
`;

// Loading component
export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgBase', '#f9fafb')};
`;

export const LoadingContent = styled.div`
  text-align: center;
`;

export const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${({ theme }) => getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
  border-top-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LoadingText = styled.p`
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  font-size: 14px;
`;

// Search input for sidebar
export const SidebarSearchContainer = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

export const SidebarSearchInput = styled.input`
  width: 100%;
  max-width: 230px;
  padding: 8px 12px 8px 36px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  border-radius: 8px;
  font-size: 14px;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
    box-shadow: 0 0 0 2px ${({ theme }) => getThemeColor(theme, 'colorPrimaryBg', 'rgba(22, 119, 255, 0.1)')};
  }

  &::placeholder {
    color: ${({ theme }) => getThemeColor(theme, 'colorTextTertiary', 'rgba(0, 0, 0, 0.25)')};
  }
`;

export const SidebarSearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  pointer-events: none;
`;

// Create button
export const CreateButton = styled.button`
  padding: 12px 24px;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
  color: #ffffff;
  border-radius: 8px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: ${({ theme }) => getThemeColor(theme, 'colorPrimaryHover', '#0958d9')};
  }
`;

// Action buttons
export const ActionButton = styled.button`
  padding: 8px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;

  ${({ $variant }) => {
    if ($variant === 'edit') {
      return `
        color: #1677ff;
        &:hover {
          background-color: #e6f4ff;
        }
      `;
    }
    if ($variant === 'delete') {
      return `
        color: #ff4d4f;
        &:hover {
          background-color: #fff1f0;
        }
      `;
    }
  }}
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

// Modal components
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  border-radius: 12px;
  padding: 20px;
  width: 100%;
  max-width: 480px;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  margin: 0;
`;

export const ModalCloseButton = styled.button`
  padding: 4px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  border-radius: 6px;
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ theme }) => getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 12px;
  padding: 6px;
`;

export const FormLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  margin-bottom: 6px;
`;

export const FormInput = styled.input`
  width: 90%;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  border-radius: 6px;
  font-size: 13px;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
    box-shadow: 0 0 0 2px ${({ theme }) => getThemeColor(theme, 'colorPrimaryBg', 'rgba(22, 119, 255, 0.1)')};
  }

  &:disabled {
    background-color: ${({ theme }) => getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
    cursor: not-allowed;
  }
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
  border-radius: 6px;
  font-size: 13px;
  background-color: ${({ theme }) => getThemeColor(theme, 'colorBgContainer', '#ffffff')};
  color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
    box-shadow: 0 0 0 2px ${({ theme }) => getThemeColor(theme, 'colorPrimaryBg', 'rgba(22, 119, 255, 0.1)')};
  }

  &:hover {
    border-color: ${({ theme }) => getThemeColor(theme, 'colorPrimary', '#1677ff')};
  }

  &:disabled {
    background-color: ${({ theme }) => getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
    cursor: not-allowed;
  }
`;

export const FormInputGroup = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

export const FormButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
`;

export const Button = styled.button`
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${({ $variant }) => {
    if ($variant === 'primary') {
      return `
        background-color: #1677ff;
        color: #ffffff;
        &:hover {
          background-color: #0958d9;
        }
      `;
    }
    if ($variant === 'danger') {
      return `
        background-color: #ff4d4f;
        color: #ffffff;
        &:hover {
          background-color: #cf1322;
        }
      `;
    }
    return `
      background-color: transparent;
      color: ${({ theme }) => getThemeColor(theme, 'colorText', 'rgba(0, 0, 0, 0.88)')};
      border: 1px solid ${({ theme }) => getThemeColor(theme, 'colorBorder', '#d9d9d9')};
      &:hover {
        background-color: ${({ theme }) => getThemeColor(theme, 'colorFillTertiary', 'rgba(0, 0, 0, 0.04)')};
      }
    `;
  }}
`;

export const ModalText = styled.p`
  color: ${({ theme }) => getThemeColor(theme, 'colorTextSecondary', 'rgba(0, 0, 0, 0.45)')};
  margin: 0 0 16px 0;
  font-size: 13px;
`;

export const SearchAndCreateContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
`;

export const TableActionHeader = styled(TableHeader)`
  text-align: right;
`;

